import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Plus, Search, QrCode, Package, Scissors, MapPin, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import inventoryService from '../services/inventoryService';
import UnifiedLabelPrintModal from './UnifiedLabelPrintModal';

const RetalhosManagerModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('DISPONIVEL');
  const [espessuraFilter, setEspessuraFilter] = useState('');
  const [minCompFilter, setMinCompFilter] = useState('');
  const [minLargFilter, setMinLargFilter] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedRetalhoForLabel, setSelectedRetalhoForLabel] = useState(null);
  const [consumeModal, setConsumeModal] = useState({ open: false, retalho: null, motivo: 'USO_PROJETO', observacoes: '' });

  // Fechar ao pressionar a tecla Escape (com suporte a sub-modais)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showNewModal) {
          setShowNewModal(false);
        } else if (consumeModal.open) {
          setConsumeModal({ open: false, retalho: null, motivo: 'USO_PROJETO', observacoes: '' });
        } else if (selectedRetalhoForLabel) {
          setSelectedRetalhoForLabel(null);
        } else {
          onClose?.();
        }
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose, showNewModal, consumeModal.open, selectedRetalhoForLabel]);

  // Query de retalhos
  const {
    data: retalhosData,
    isPending: loadingRetalhos,
    refetch
  } = useQuery({
    queryKey: ['inventory-retalhos', statusFilter, search, espessuraFilter, minCompFilter, minLargFilter],
    queryFn: () => inventoryService.getRetalhos({
      status: statusFilter,
      search: search || undefined,
      espessura_mm: espessuraFilter || undefined,
      min_comprimento: minCompFilter || undefined,
      min_largura: minLargFilter || undefined,
      limit: 100
    }),
    enabled: isOpen
  });

  const retalhos = retalhosData?.data || [];
  const totalAreaDisponivel = retalhos
    .filter(r => r.status === 'DISPONIVEL')
    .reduce((sum, r) => sum + (parseFloat(r.area_m2) || 0), 0);

  // Mutation para consumir retalho
  const consumeMutation = useMutation({
    mutationFn: ({ id, data }) => inventoryService.consumeRetalho(id, data),
    onSuccess: (res) => {
      toast.success(res.message || 'Pedaço consumido com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['inventory-retalhos'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-stats'] });
      setConsumeModal({ open: false, retalho: null, motivo: 'USO_PROJETO', observacoes: '' });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao dar baixa no pedaço');
    }
  });

  const handlePrintLabel = (retalho) => {
    setSelectedRetalhoForLabel(retalho);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4 overflow-hidden">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[94vh] flex flex-col overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50/80 via-white to-orange-50/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow-sm">
              <Scissors className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                Banco de Retalhos e Sobras Úteis (MDF / Peças)
              </h2>
              <p className="text-xs text-gray-500">
                Rastreamento individual de pedaços cortados com medidas precisas, localização e etiquetas QR Code.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNewModal(true)}
              className="px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1.5 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Cadastrar Sobra / Pedaço</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg transition-colors"
              title="Fechar (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* KPIs Resumo das Sobras */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-gray-50/70 border-b border-gray-200">
          <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
            <span className="text-[11px] font-medium text-gray-500 block">Pedaços Disponíveis</span>
            <span className="text-xl font-bold text-amber-600">
              {retalhos.filter(r => r.status === 'DISPONIVEL').length} peças
            </span>
          </div>
          <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
            <span className="text-[11px] font-medium text-gray-500 block">Área Útil em Sobras</span>
            <span className="text-xl font-bold text-emerald-600">
              {totalAreaDisponivel.toFixed(2)} m²
            </span>
          </div>
          <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
            <span className="text-[11px] font-medium text-gray-500 block">Equivalente em Chapas</span>
            <span className="text-xl font-bold text-blue-600">
              ~{(totalAreaDisponivel / 5.0875).toFixed(1)} chapas
            </span>
          </div>
          <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
            <span className="text-[11px] font-medium text-gray-500 block">Economia Estimada</span>
            <span className="text-xl font-bold text-purple-600">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((totalAreaDisponivel / 5.0875) * 320)}
            </span>
          </div>
        </div>

        {/* Barra de Filtros */}
        <div className="p-4 border-b border-gray-200 bg-white grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div className="sm:col-span-2 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código, cor, MDF ou localização..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
            >
              <option value="ALL">Todos os Status</option>
              <option value="DISPONIVEL">Apenas Disponíveis</option>
              <option value="RESERVADO">Reservados para Corte</option>
              <option value="CONSUMIDO">Já Consumidos</option>
            </select>
          </div>

          <div>
            <input
              type="number"
              step="1"
              value={minCompFilter}
              onChange={(e) => setMinCompFilter(e.target.value)}
              placeholder="Comp. Mín. (cm)"
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
          </div>

          <div className="flex gap-2">
            <input
              type="number"
              step="1"
              value={minLargFilter}
              onChange={(e) => setMinLargFilter(e.target.value)}
              placeholder="Larg. Mín. (cm)"
              className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('DISPONIVEL');
                setEspessuraFilter('');
                setMinCompFilter('');
                setMinLargFilter('');
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg text-xs"
              title="Limpar filtros"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabela de Retalhos */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingRetalhos ? (
            <div className="py-16 text-center text-gray-400 text-sm">Carregando pedaços...</div>
          ) : retalhos.length === 0 ? (
            <div className="py-16 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-300">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-700">Nenhum pedaço/retalho encontrado</p>
              <p className="text-xs text-gray-400 mt-1">Cadastre as sobras de cortes anteriores para reaproveitá-las em próximos projetos.</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-xl">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Código Único</th>
                    <th className="px-3 py-2.5 text-left">Material / Cor</th>
                    <th className="px-3 py-2.5 text-center">Espessura</th>
                    <th className="px-3 py-2.5 text-center">Dimensões ($C \times L$)</th>
                    <th className="px-3 py-2.5 text-right">Área Útil</th>
                    <th className="px-3 py-2.5 text-left">Localização</th>
                    <th className="px-3 py-2.5 text-center">Status</th>
                    <th className="px-3 py-2.5 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {retalhos.map((r) => (
                    <tr key={r.id} className="hover:bg-amber-50/20 transition-colors">
                      <td className="px-3 py-2.5 font-mono font-bold text-gray-900">
                        {r.codigo_etiqueta}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-semibold text-gray-900 block">{r.produto_nome}</span>
                        <span className="text-[11px] text-gray-500">{r.cor || 'Padrão'} {r.acabamento ? `• ${r.acabamento}` : ''}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center font-bold text-gray-800">
                        {r.espessura_mm} mm
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="px-2 py-1 rounded bg-gray-100 font-bold text-gray-800">
                          {r.comprimento_cm} cm × {r.largura_cm} cm
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-emerald-700">
                        {r.area_m2} m²
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="flex items-center gap-1 text-gray-700 font-medium">
                          <MapPin className="w-3 h-3 text-amber-600" />
                          {r.localizacao || r.armazem_nome || 'Oficina'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          r.status === 'DISPONIVEL' ? 'bg-emerald-100 text-emerald-800' :
                          r.status === 'RESERVADO' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {r.status === 'DISPONIVEL' ? 'Disponível' :
                           r.status === 'RESERVADO' ? 'Reservado' : 'Consumido'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handlePrintLabel(r)}
                            className="p-1.5 text-amber-700 hover:bg-amber-100 rounded-lg transition-colors border border-amber-200"
                            title="Imprimir Etiqueta com QR Code"
                          >
                            <QrCode className="w-3.5 h-3.5" />
                          </button>
                          {r.status === 'DISPONIVEL' && (
                            <button
                              onClick={() => setConsumeModal({ open: true, retalho: r, motivo: 'USO_PROJETO', observacoes: '' })}
                              className="px-2 py-1 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded font-semibold text-[10px] border border-purple-200"
                              title="Dar baixa / Consumir peça"
                            >
                              Baixar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-semibold rounded-lg"
          >
            Fechar
          </button>
        </div>
      </div>

      {/* Modal para Cadastro de Nova Sobra */}
      {showNewModal && (
        <RetalhoFormModal
          isOpen={showNewModal}
          onClose={() => setShowNewModal(false)}
          onSuccess={() => {
            setShowNewModal(false);
            refetch();
          }}
        />
      )}

      {/* Modal para Baixa de Retalho */}
      {consumeModal.open && consumeModal.retalho && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
            <h3 className="text-base font-bold text-gray-900">
              Dar Baixa no Pedaço de Peça
            </h3>
            <p className="text-xs text-gray-600">
              Você está baixando a peça: <strong className="font-mono text-gray-900">{consumeModal.retalho.codigo_etiqueta}</strong> ({consumeModal.retalho.comprimento_cm}x{consumeModal.retalho.largura_cm}cm).
            </p>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Motivo da Baixa</label>
              <select
                value={consumeModal.motivo}
                onChange={(e) => setConsumeModal(prev => ({ ...prev, motivo: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              >
                <option value="USO_PROJETO">Utilizado em Projeto / Corte</option>
                <option value="DESCARTE">Descarte / Peça Danificada</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Observações</label>
              <input
                type="text"
                value={consumeModal.observacoes}
                onChange={(e) => setConsumeModal(prev => ({ ...prev, observacoes: e.target.value }))}
                placeholder="Ex: Usado na gaveta do cliente X"
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConsumeModal({ open: false, retalho: null, motivo: 'USO_PROJETO', observacoes: '' })}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => consumeMutation.mutate({
                  id: consumeModal.retalho.id,
                  data: { motivo: consumeModal.motivo, observacoes: consumeModal.observacoes }
                })}
                disabled={consumeMutation.isPending}
                className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg shadow-sm"
              >
                {consumeMutation.isPending ? 'Baixando...' : 'Confirmar Baixa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Impressão Térmica Padronizada com Tamanhos e Códigos */}
      {selectedRetalhoForLabel && (
        <UnifiedLabelPrintModal
          isOpen={Boolean(selectedRetalhoForLabel)}
          onClose={() => setSelectedRetalhoForLabel(null)}
          labels={[{
            id: selectedRetalhoForLabel.id,
            codigo_etiqueta: selectedRetalhoForLabel.codigo_etiqueta,
            label_code: selectedRetalhoForLabel.codigo_etiqueta,
            produto_nome: selectedRetalhoForLabel.produto_nome,
            cor: selectedRetalhoForLabel.cor,
            comprimento_cm: selectedRetalhoForLabel.comprimento_cm,
            largura_cm: selectedRetalhoForLabel.largura_cm,
            espessura_mm: selectedRetalhoForLabel.espessura_mm,
            area_m2: selectedRetalhoForLabel.area_m2,
            localizacao: selectedRetalhoForLabel.localizacao,
            sentido_veio: selectedRetalhoForLabel.sentido_veio
          }]}
          title={`Etiqueta de Sobra / Retalho (${selectedRetalhoForLabel.codigo_etiqueta})`}
          sourceType="RETALHO"
        />
      )}
    </div>
  );
};

// Sub-componente de Cadastro de Nova Sobra
const RetalhoFormModal = ({ isOpen, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    produto_id: '',
    variante_id: '',
    armazem_id: '',
    comprimento_cm: '',
    largura_cm: '',
    espessura_mm: '',
    sentido_veio: 'SEM_VEIO',
    localizacao: '',
    lote_origem: '',
    observacoes: ''
  });

  const { data: productsData } = useQuery({
    queryKey: ['inventory-products-for-retalhos'],
    queryFn: () => inventoryService.getProducts({ limit: 100 })
  });

  const { data: warehousesData } = useQuery({
    queryKey: ['inventory-warehouses'],
    queryFn: inventoryService.getWarehouses
  });

  const products = productsData?.data || [];
  const warehouses = warehousesData?.data || [];

  const selectedProduct = products.find(p => p.id === parseInt(formData.produto_id, 10));
  const variants = selectedProduct?.variantes || [];

  const createMutation = useMutation({
    mutationFn: (data) => inventoryService.createRetalho(data),
    onSuccess: (res) => {
      toast.success(res.message || 'Sobra cadastrada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['inventory-retalhos'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-stats'] });
      onSuccess?.();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao cadastrar sobra');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.produto_id || !formData.variante_id || !formData.armazem_id) {
      toast.error('Selecione o produto, variante e armazém');
      return;
    }
    if (!formData.comprimento_cm || !formData.largura_cm) {
      toast.error('Informe o comprimento e largura em centímetros');
      return;
    }

    createMutation.mutate({
      produto_id: parseInt(formData.produto_id, 10),
      variante_id: parseInt(formData.variante_id, 10),
      armazem_id: parseInt(formData.armazem_id, 10),
      comprimento_cm: parseFloat(formData.comprimento_cm),
      largura_cm: parseFloat(formData.largura_cm),
      espessura_mm: formData.espessura_mm ? parseFloat(formData.espessura_mm) : undefined,
      sentido_veio: formData.sentido_veio,
      localizacao: formData.localizacao,
      lote_origem: formData.lote_origem,
      observacoes: formData.observacoes
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-amber-50/80 flex justify-between items-center">
          <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
            <Scissors className="w-5 h-5 text-amber-600" />
            Cadastrar Pedaço / Sobra de MDF
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-gray-700 mb-1">Produto / Chapa de Origem *</label>
            <select
              required
              value={formData.produto_id}
              onChange={(e) => setFormData({ ...formData, produto_id: e.target.value, variante_id: '' })}
              className="w-full p-2 border border-gray-300 rounded-lg"
            >
              <option value="">Selecione a chapa...</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.nome} ({p.sku || 'Sem SKU'})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">Variante / Cor *</label>
              <select
                required
                disabled={!formData.produto_id}
                value={formData.variante_id}
                onChange={(e) => {
                  const v = variants.find(item => item.id === parseInt(e.target.value, 10));
                  setFormData({
                    ...formData,
                    variante_id: e.target.value,
                    espessura_mm: v?.espessura_mm || formData.espessura_mm
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
              >
                <option value="">Selecione...</option>
                {variants.map(v => (
                  <option key={v.id} value={v.id}>{v.cor || v.sku_variacao || `Variante #${v.id}`} ({v.espessura_mm}mm)</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">Armazém / Local Guarda *</label>
              <select
                required
                value={formData.armazem_id}
                onChange={(e) => setFormData({ ...formData, armazem_id: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="">Selecione...</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.nome}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">Comprimento (cm) *</label>
              <input
                type="number"
                step="0.1"
                required
                placeholder="Ex: 150"
                value={formData.comprimento_cm}
                onChange={(e) => setFormData({ ...formData, comprimento_cm: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg font-bold text-gray-900"
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">Largura (cm) *</label>
              <input
                type="number"
                step="0.1"
                required
                placeholder="Ex: 82"
                value={formData.largura_cm}
                onChange={(e) => setFormData({ ...formData, largura_cm: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg font-bold text-gray-900"
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">Espessura (mm)</label>
              <input
                type="number"
                step="1"
                placeholder="Ex: 15 ou 18"
                value={formData.espessura_mm}
                onChange={(e) => setFormData({ ...formData, espessura_mm: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg font-bold text-gray-900"
              />
            </div>
          </div>

          {formData.comprimento_cm && formData.largura_cm && (
            <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 font-medium flex justify-between items-center">
              <span>Área calculada da peça:</span>
              <strong className="text-sm">
                {((parseFloat(formData.comprimento_cm) * parseFloat(formData.largura_cm)) / 10000).toFixed(4)} m²
              </strong>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">Localização no Galpão</label>
              <input
                type="text"
                placeholder="Ex: Cavalete A - Baia 2"
                value={formData.localizacao}
                onChange={(e) => setFormData({ ...formData, localizacao: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">Sentido do Veio</label>
              <select
                value={formData.sentido_veio}
                onChange={(e) => setFormData({ ...formData, sentido_veio: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="SEM_VEIO">Sem veio / Unicolor</option>
                <option value="LONGITUDINAL">Longitudinal (No Comprimento)</option>
                <option value="TRANSVERSAL">Transversal (Na Largura)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-1">Observações da Peça</label>
            <input
              type="text"
              placeholder="Ex: Sobra do corte da cozinha do cliente X, sem avarias"
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="pt-3 border-t border-gray-200 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold shadow-sm disabled:opacity-50"
            >
              {createMutation.isPending ? 'Cadastrando...' : 'Gerar Código e Salvar Peça'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RetalhosManagerModal;
